import type { Track } from './bridge';

type PlaylistArtwork = { customCover?: string; trackIds: string[] };

/** Same original-track candidate rules as runtime.cover, without its SVG fallback. */
export function trackArtworkCandidate(track?: Track, resourceUrl: (url: string) => string | null = value => value): string {
  if (track?.sourceType === 'local' && track.customCover) return track.customCover;
  if (track?.sourceType === 'online' && track.metadata?.neteaseCloud?.accountOnly !== true && ['yuxi', 'yuningxi'].includes(track.sourceId)) {
    const platform = track.metadata?.platform === 'tx' || track.id.split(':')[2] === 'tx' ? 'tx' : 'wy';
    if (platform === 'tx' || track.metadata?.artworkKnownMissing !== true) {
      const url = resourceUrl(`/api/artwork/${platform}/${encodeURIComponent(track.externalId)}${platform === 'tx' ? '?v=2' : ''}`);
      if (url) return url;
    }
  }
  return track?.metadata?.artworkKnownMissing === true ? '' : track?.artworkRef || '';
}

export function playlistArtworkCandidates(playlist: PlaylistArtwork, tracks: Track[], candidate: (track: Track) => string = trackArtworkCandidate): string[] {
  const byId = new Map(tracks.map(track => [track.id, track]));
  const values = playlist.customCover ? [playlist.customCover] : [];
  for (const id of [...playlist.trackIds].reverse()) {
    const track = byId.get(id), url = track && candidate(track);
    if (url) values.push(url);
  }
  return [...new Set(values)];
}

/**
 * A transparent, local presentation fallback for platform playlists whose
 * provider returned no cover. It only uses artwork candidates from the exact
 * tracks in this playlist; it never substitutes another playlist or title.
 */
export function generatedPlaylistArtwork(tracks: Track[], candidate: (track: Track) => string = trackArtworkCandidate): string {
  const images = tracks.map(track => candidate(track)).filter(Boolean).slice(0, 4);
  if (!images.length) return '';
  const cells = images.map((url, index) => {
    const x = index % 2 * 200, y = Math.floor(index / 2) * 200;
    return `<image href="${escapeSvgAttribute(url)}" x="${x}" y="${y}" width="200" height="200" preserveAspectRatio="xMidYMid slice"/>`;
  }).join('');
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">${cells}</svg>`)}`;
}

function escapeSvgAttribute(value: string) {
  return value.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[character] || character));
}
